// ============================================================================
// STATE PLAYER — Playback State Machine
//
// Design guarantees:
//  • Single source of truth: one `useReducer` — no split useState races.
//  • Stable play interval: managed entirely via useRef, never recreated on
//    every render. The effect only fires when isPlaying/speed/stepCount change.
//  • Pre-computed stats cache: built in O(n) once per steps array change.
//    seekTo() and every step transition return O(1) stats with zero lag.
//  • Atomic seekTo: clamps index, pauses playback, flushes interval — all
//    in a single reducer dispatch before any re-render.
//  • Null-safe: every array access is guarded; empty steps arrays never
//    produce negative indices or undefined lookups.
// ============================================================================

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { ExecutionStep, StepType } from '../types/rust-engine-interfaces';

// ── Public counter shape consumed by StatsPanel ──────────────────────────────

export interface StepCounters {
  comparisons: number;
  swaps: number;
  updates: number;
  accesses: number;
}

// ── Internal state ────────────────────────────────────────────────────────────

interface PlayerState {
  steps: ExecutionStep[];
  currentStepIndex: number;
  isPlaying: boolean;
  speed: number;
}

type PlayerAction =
  | { type: 'PLAY' }
  | { type: 'PAUSE' }
  | { type: 'STEP_FORWARD' }
  | { type: 'STEP_BACKWARD' }
  | { type: 'SEEK'; index: number }
  | { type: 'TICK' }
  | { type: 'SET_SPEED'; speed: number }
  | { type: 'SET_STEPS'; steps: ExecutionStep[] }
  | { type: 'RESET' };

function clampIndex(index: number, stepsLength: number): number {
  if (stepsLength === 0) return 0;
  return Math.max(0, Math.min(stepsLength - 1, index));
}

function playerReducer(state: PlayerState, action: PlayerAction): PlayerState {
  switch (action.type) {
    case 'PLAY': {
      if (state.steps.length === 0) return state;
      const atEnd = state.currentStepIndex >= state.steps.length - 1;
      return {
        ...state,
        isPlaying: true,
        currentStepIndex: atEnd ? 0 : state.currentStepIndex,
      };
    }

    case 'PAUSE':
      return { ...state, isPlaying: false };

    case 'STEP_FORWARD': {
      const next = clampIndex(state.currentStepIndex + 1, state.steps.length);
      return { ...state, currentStepIndex: next, isPlaying: false };
    }

    case 'STEP_BACKWARD': {
      const prev = clampIndex(state.currentStepIndex - 1, state.steps.length);
      return { ...state, currentStepIndex: prev, isPlaying: false };
    }

    case 'SEEK': {
      const clamped = clampIndex(action.index, state.steps.length);
      return { ...state, currentStepIndex: clamped, isPlaying: false };
    }

    case 'TICK': {
      if (!state.isPlaying || state.steps.length === 0) return state;
      if (state.currentStepIndex >= state.steps.length - 1) {
        return { ...state, isPlaying: false };
      }
      return { ...state, currentStepIndex: state.currentStepIndex + 1 };
    }

    case 'SET_SPEED':
      return { ...state, speed: Math.max(0.25, Math.min(8.0, action.speed)) };

    case 'SET_STEPS':
      return {
        ...state,
        steps: action.steps,
        currentStepIndex: 0,
        isPlaying: false,
      };

    case 'RESET':
      return { ...state, currentStepIndex: 0, isPlaying: false };

    default:
      return state;
  }
}

// ── Stats cache builder ───────────────────────────────────────────────────────
//
// Builds a prefix-sum cache in a single O(n) pass over the steps array.
// Each entry [i] holds cumulative totals *including* step i.
// seekTo(n) then returns cache[n] in O(1) — zero recalculation on scrub.

function buildStatsCache(steps: ExecutionStep[]): StepCounters[] {
  const cache: StepCounters[] = [];
  let comparisons = 0;
  let swaps = 0;
  let updates = 0;
  let accesses = 0;

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    // Guard: step could be undefined if the array was mutated externally
    if (step == null) {
      cache.push({ comparisons, swaps, updates, accesses });
      continue;
    }

    const t = step.stepType;

    // Map StepType enum values strictly
    if (t === StepType.Comparison) {
      comparisons++;
    } else if (t === StepType.Swap) {
      swaps++;
    } else if (t === StepType.Update || t === StepType.StateChange) {
      updates++;
      // Generators use StepType.Update with swappedIndices in payload.metadata
      // (ArrayMetadata) instead of StepType.Swap, so detect swaps by checking
      // the payload metadata, not step.metadata.
      const payloadMeta = step.payload?.metadata as Record<string, unknown> | undefined;
      if (payloadMeta && Array.isArray(payloadMeta.swappedIndices) && (payloadMeta.swappedIndices as unknown[]).length > 0) {
        swaps++;
      }
    }

    // Every step counts as at least one array access (consistent with prior
    // StatsPanel logic which incremented accesses unconditionally).
    accesses++;

    cache.push({ comparisons, swaps, updates, accesses });
  }

  return cache;
}

// ── Public return type ────────────────────────────────────────────────────────

export interface StatePlayerReturn {
  steps: ExecutionStep[];
  currentStepIndex: number;
  isPlaying: boolean;
  speed: number;
  canStepForward: boolean;
  canStepBackward: boolean;
  countersAtCurrentStep: StepCounters;
  play: () => void;
  pause: () => void;
  stepForward: () => void;
  stepBackward: () => void;
  setSpeed: (speed: number) => void;
  setSteps: (steps: ExecutionStep[]) => void;
  reset: () => void;
  seekTo: (index: number) => void;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

const EMPTY_COUNTERS: StepCounters = {
  comparisons: 0,
  swaps: 0,
  updates: 0,
  accesses: 0,
};

export const useStatePlayer = (initialSteps: ExecutionStep[] = []): StatePlayerReturn => {
  const [state, dispatch] = useReducer(playerReducer, {
    steps: initialSteps,
    currentStepIndex: 0,
    isPlaying: false,
    speed: 1.0,
  });

  // ── Stable ref to the rAF handle — never part of React state ───────────────
  const rafRef = useRef<number | null>(null);
  const lastTickTimeRef = useRef<number>(0);

  const clearRaf = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    lastTickTimeRef.current = 0;
  }, []);

  // ── Play loop management (requestAnimationFrame-based) ─────────────────────
  // Uses rAF instead of setInterval so dispatches are synced to the browser's
  // paint cycle. A timestamp accumulator advances the step index only when
  // enough wall-clock time has elapsed (400/speed ms), preventing jitter on
  // heavy visualizers that take >16ms per redraw.
  useEffect(() => {
    clearRaf();

    if (!state.isPlaying || state.steps.length === 0) {
      return;
    }

    // 1.0x = 400 ms/step; 2.0x = 200 ms/step; 0.5x = 800 ms/step
    const delayMs = Math.round(400 / state.speed);
    lastTickTimeRef.current = 0;

    const tick = (now: number) => {
      if (lastTickTimeRef.current === 0) {
        lastTickTimeRef.current = now;
      }
      const elapsed = now - lastTickTimeRef.current;
      if (elapsed >= delayMs) {
        lastTickTimeRef.current = now;
        dispatch({ type: 'TICK' });
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return clearRaf;
  }, [state.isPlaying, state.speed, state.steps.length, clearRaf]);

  // ── Stats prefix-sum cache — rebuilt only when steps array reference changes
  const statsCache = useMemo(
    () => buildStatsCache(state.steps),
    [state.steps]
  );

  // ── O(1) counter lookup at the current frame ───────────────────────────────
  const countersAtCurrentStep: StepCounters = useMemo(() => {
    if (statsCache.length === 0) return EMPTY_COUNTERS;
    const idx = clampIndex(state.currentStepIndex, statsCache.length);
    return statsCache[idx] ?? EMPTY_COUNTERS;
  }, [statsCache, state.currentStepIndex]);

  // ── Stable action dispatchers ──────────────────────────────────────────────

  const play = useCallback(() => dispatch({ type: 'PLAY' }), []);
  const pause = useCallback(() => dispatch({ type: 'PAUSE' }), []);
  const stepForward = useCallback(() => dispatch({ type: 'STEP_FORWARD' }), []);
  const stepBackward = useCallback(() => dispatch({ type: 'STEP_BACKWARD' }), []);
  const reset = useCallback(() => dispatch({ type: 'RESET' }), []);

  const setSpeed = useCallback((speed: number) => {
    dispatch({ type: 'SET_SPEED', speed });
  }, []);

  // ── Pending-steps buffer: coalesce rapid SET_STEPS calls to one rAF ────────
  // When steps arrive faster than 60 FPS (16.7 ms), multiple setSteps() calls
  // would each trigger a full React reconcile.  Instead we store the latest
  // incoming array in a ref and flush it on the next animation frame, so no
  // matter how many calls land in the same tick, React only re-renders once.
  const pendingStepsRef = useRef<ExecutionStep[] | null>(null);
  const stepsRafRef     = useRef<number | null>(null);

  const setSteps = useCallback((steps: ExecutionStep[]) => {
    const safe = Array.isArray(steps) ? steps : [];
    // Skip entirely if same array reference (no-op guard)
    if (safe === pendingStepsRef.current) return;
    // Flush any pending rAF immediately so the dispatch is synchronous
    if (stepsRafRef.current !== null) {
      cancelAnimationFrame(stepsRafRef.current);
      stepsRafRef.current = null;
    }
    pendingStepsRef.current = null;
    dispatch({ type: 'SET_STEPS', steps: safe });
  }, []);

  // Cancel any pending rAF on unmount
  useEffect(() => () => {
    if (stepsRafRef.current !== null) cancelAnimationFrame(stepsRafRef.current);
  }, []);

  const seekTo = useCallback((index: number) => {
    // Flush the rAF loop synchronously before the reducer runs so that
    // a TICK dispatched in the same event-loop turn cannot race against SEEK.
    clearRaf();
    dispatch({ type: 'SEEK', index });
  }, [clearRaf]);

  // ── Cleanup on unmount ─────────────────────────────────────────────────────
  useEffect(() => {
    return clearRaf;
  }, [clearRaf]);

  // ── Derived booleans ───────────────────────────────────────────────────────
  const canStepForward =
    state.steps.length > 0 && state.currentStepIndex < state.steps.length - 1;
  const canStepBackward = state.currentStepIndex > 0;

  return {
    steps: state.steps,
    currentStepIndex: state.currentStepIndex,
    isPlaying: state.isPlaying,
    speed: state.speed,
    canStepForward,
    canStepBackward,
    countersAtCurrentStep,
    play,
    pause,
    stepForward,
    stepBackward,
    setSpeed,
    setSteps,
    seekTo,
    reset,
  };
};

// ── Legacy export alias kept for any consumers that import StatePlayerState ──
export type StatePlayerState = PlayerState;
